import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { G, TIERS } from "@/brand";
import api, { apiError } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";

export default function Pricing() {
  const { user, requireAuth } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  const owned = user?.access_tier || "free";
  const rank = { free: 0, vault: 1, codex: 2, upgrade_1: 1, upgrade_2: 2 };
  const tierKey = { vault: "upgrade_1", codex: "upgrade_2" };

  const buy = async (tier) => {
    if (tier.id === "free") { nav("/library"); return; }
    if (!requireAuth(`Sign in to upgrade to ${tier.name}.`)) return;
    setError(""); setLoading(tier.id);
    try {
      const { data } = await api.post("/checkout", { package_id: tier.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
      setLoading("");
    }
  };

  return (
    <div style={{ background: G.black, minHeight: "100vh" }}>
      <Nav />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "130px 5vw 80px" }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 14 }}>THREE TIERS · ONE DECISION</div>
        <h1 style={{ fontSize: "clamp(32px,5vw,56px)", fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 14 }}>
          Start free. Go deep.
        </h1>
        <p style={{ color: G.muted, fontStyle: "italic", fontSize: 17, marginBottom: 16 }}>
          One-time purchases. No subscription. Lifetime access. Prices shown add 6.5% NC tax at checkout.
        </p>
        {error && <div className="mono" style={{ color: "#ff5555", fontSize: 12, marginBottom: 16 }}>{error}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))", gap: 24, marginTop: 30 }}>
          {TIERS.map((t) => {
            const isOwned = rank[owned] >= rank[tierKey[t.id] || "free"] && t.id !== "free";
            return (
              <div key={t.id} data-testid={`pricing-tier-${t.id}`} className="tier-card" style={{ background: G.tier, border: `1px solid ${t.badge ? G.orange : G.border}`, padding: "36px 30px", position: "relative" }}>
                {t.badge && <div className="mono" style={{ position: "absolute", top: -1, right: 24, background: G.orange, color: G.black, fontSize: 8, letterSpacing: "0.25em", padding: "5px 12px", fontWeight: 700 }}>★ {t.badge}</div>}
                <div className="mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: G.goldDim }}>{t.sub}</div>
                <div style={{ fontSize: 26, color: "#fff", marginTop: 8, fontStyle: "italic" }}>{t.name}</div>
                <div style={{ fontSize: 50, fontWeight: 700, color: t.accent, marginTop: 16 }}>{t.price}</div>
                <div className="mono" style={{ fontSize: 9, letterSpacing: "0.12em", color: G.goldDim, marginTop: 4 }}>{t.priceSub}</div>
                <div style={{ color: G.muted, fontSize: 14.5, marginTop: 18, lineHeight: 1.6, fontStyle: "italic", minHeight: 66 }}>{t.desc}</div>
                <div style={{ borderTop: `1px solid ${G.border}44`, margin: "20px 0", paddingTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                  {t.features.map((f) => (
                    <div key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <Check size={15} color={t.accent} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span style={{ fontSize: 14, color: G.text }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button data-testid={`pricing-cta-${t.id}`} onClick={() => buy(t)} disabled={loading === t.id || isOwned}
                  className={t.outline ? "btn-outline" : t.badge ? "btn-orange" : "btn-gold"} style={{ width: "100%", padding: 14, fontSize: 10 }}>
                  {isOwned ? "✓ Owned" : loading === t.id ? "Redirecting…" : t.cta}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
